import { BodyText, Icon } from '@postscript/components';
import { getSystemMessageIcon } from 'components/responses/utils/messages';
import { isBubAI } from 'components/sales/pages/utils';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useShopperStatus } from 'hooks/useShopperStatus';
import moment from 'moment';
import React, { useState } from 'react';
import styled from 'styled-components';

interface ProfileEventProps {
  message: {
    text: string;
    direction?: string;
    eventData?: {
      description?: string;
      // eslint-disable-next-line camelcase
      public_metadata?: string;
      [key: string]: any;
    };
    // eslint-disable-next-line camelcase
    created_at?: string;
    [key: string]: any;
  };
}

const Container = styled.div`
  display: flex;
  align-items: center;
  background: var(--blue-1);
  border-radius: var(--border-radius-medium);
  padding: 10px 24px;
  gap: 12px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--blue-5);
  font-size: 1.5rem;
  margin-top: 2px;
  min-width: var(--spacing-4);
  max-width: var(--spacing-4);
  height: var(--spacing-4);
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
`;

const ProfileEvent: React.FC<ProfileEventProps> = ({ message }) => {
  const { isLive } = useShopperStatus();
  const { hasLabsFlag } = usePSLabs();
  const isSender = !!(isLive && hasLabsFlag('ai_managed_outbound'));

  if (!isBubAI() && !isSender) {
    return null;
  }

  const formatTimestamp = (timestamp: any): string | null => {
    try {
      const parsedMoment = moment(timestamp, 'YYYY-MM-DD HH:mm:ss', true);
      return parsedMoment.isValid()
        ? parsedMoment.format('MM/DD/YY, h:mm A CT')
        : null;
    } catch (error) {
      console.warn(`Error parsing timestamp:`, timestamp, error);
      return null;
    }
  };

  const getDisplayText = (): { displayText: string; iconText: string } => {
    const { description, public_metadata: publicMetadata } =
      message.eventData || {};

    if (!description || !publicMetadata) {
      return { displayText: message.text, iconText: message.text };
    }

    const timestamp = message.createdAt
      ? formatTimestamp(message.createdAt)
      : null;
    const displayText = timestamp ? `${description} ${timestamp}` : description;

    return { displayText, iconText: description };
  };

  const { displayText, iconText } = getDisplayText();
  const resolvedIcon = getSystemMessageIcon(iconText);
  const showAccordion = !!(
    message.eventData?.description && message.eventData?.public_metadata
  );

  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <button
        type="button"
        style={{
          background: 'none',
          border: 'none',
          width: '100%',
          padding: 0,
          margin: 0,
          cursor: showAccordion ? 'pointer' : 'default',
          display: 'block',
          textAlign: 'inherit',
        }}
        aria-expanded={showAccordion ? expanded : undefined}
        aria-controls={showAccordion ? 'profile-event-details' : undefined}
        onClick={showAccordion ? () => setExpanded((prev) => !prev) : undefined}
        tabIndex={0}
        disabled={!showAccordion}
      >
        <Container>
          <IconWrapper>
            <Icon color="var(--blue-5)" component={resolvedIcon} size={18} />
          </IconWrapper>
          <ContentWrapper>
            <BodyText size="x-small">{displayText}</BodyText>
          </ContentWrapper>
          {showAccordion && (
            <span
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <path
                  d="M6 8L10 12L14 8"
                  stroke="#222"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          )}
        </Container>
      </button>
      {showAccordion && expanded && (
        <div
          id="profile-event-details"
          style={{
            background: 'var(--blue-1)',
            borderRadius:
              '0 0 var(--border-radius-medium) var(--border-radius-medium)',
            marginTop: '-8px',
            padding: '0 24px 10px 56px', // left padding to align with text
          }}
        >
          <BodyText size="x-small">
            {message.eventData?.public_metadata}
          </BodyText>
        </div>
      )}
    </>
  );
};

export default ProfileEvent;
